import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { PackageSearch, Ruler, RefreshCcw, Truck, Sparkles } from "lucide-react";
import { API, fetcher } from "../lib/api";
import { useSeo, productJsonLd } from "../lib/seo";
import { CATEGORIES, SIZE_ROWS, SIZE_TABS } from "../data/site";
import ProductCard from "../components/ProductCard";

const easeOut = [0.16, 1, 0.3, 1];

const cmToIn = (cm) => Math.round((cm / 2.54) * 2) / 2;

function DeliveryReturns() {
  return (
    <section id="delivery" className="scroll-mt-24 py-20 md:py-24" data-testid="delivery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: easeOut }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
            Good to Know
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Delivery & Returns
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55, ease: easeOut }}
            data-testid="delivery-card"
            className="rounded-[32px] border border-baby-100 bg-baby-50 p-8"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-baby-600 shadow-sm">
              <Truck className="h-5 w-5" />
            </span>
            <h3 className="mt-5 font-display text-xl font-semibold text-ink">
              Delivery Information
            </h3>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-ink/75">
              <li>
                <strong className="text-ink">Standard Delivery</strong> (3–5 business days) — free on orders over £50
              </li>
              <li>
                <strong className="text-ink">Express Spark Delivery</strong> (1–2 business days) — £8.99
              </li>
              <li>
                <strong className="text-ink">Live tracking</strong> — SMS &amp; email updates on every shipment
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55, ease: easeOut, delay: 0.08 }}
            data-testid="returns-card"
            className="rounded-[32px] border border-mint-100 bg-mint-50 p-8"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-mint-600 shadow-sm">
              <RefreshCcw className="h-5 w-5" />
            </span>
            <h3 className="mt-5 font-display text-xl font-semibold text-ink">
              Returns Information
            </h3>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-ink/75">
              <li>
                <strong className="text-ink">30-day happiness guarantee</strong> — no-questions returns
              </li>
              <li>
                <strong className="text-ink">Prepaid label</strong> included in every box
              </li>
              <li>
                <strong className="text-ink">Fast refunds</strong> — store credit instantly, or refund within 48 hours of scan
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SizeGuide() {
  const [tab, setTab] = useState(SIZE_TABS[0].id);
  const [unit, setUnit] = useState("cm");
  const [height, setHeight] = useState("");

  const activeTab = SIZE_TABS.find((t) => t.id === tab);

  const recommended = useMemo(() => {
    const h = parseInt(height, 10);
    if (!h || h < 50 || h > 170) return null;
    const inRange = SIZE_ROWS.find(
      (r) => h >= r.height[0] - 4 && h <= r.height[1] + 4
    );
    if (inRange) return inRange.label;
    return SIZE_ROWS.reduce(
      (best, r) =>
        Math.abs((r.height[0] + r.height[1]) / 2 - h) <
        Math.abs((best.height[0] + best.height[1]) / 2 - h)
          ? r
          : best,
      SIZE_ROWS[0]
    ).label;
  }, [height]);

  const fmt = (cm) => (unit === "cm" ? `${cm}` : `${cmToIn(cm)}`);
  const fmtHeight = (range) =>
    unit === "cm" ? `${range[0]}–${range[1]}` : `${cmToIn(range[0])}–${cmToIn(range[1])}`;

  return (
    <section
      id="size-guide"
      className="scroll-mt-24 bg-blush-50/70 py-20 md:py-24"
      data-testid="size-guide-section"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: easeOut }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
              Fit Checker
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Size Guide
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted">
              Measurements are body measurements in centimetres. Between
              sizes? We recommend sizing up for growing sparklers.
            </p>
          </div>
          <div className="inline-flex rounded-full border border-lav-100 bg-white p-1">
            {["cm", "in"].map((u) => (
              <button
                key={u}
                data-testid={`unit-toggle-${u}`}
                onClick={() => setUnit(u)}
                className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
                  unit === u ? "bg-ink text-white" : "text-muted hover:text-ink"
                }`}
              >
                {u === "cm" ? "CM" : "Inches"}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, ease: easeOut }}
            className="overflow-hidden rounded-[32px] border border-lav-100 bg-white lg:col-span-8"
            data-testid="size-table-card"
          >
            <div className="flex gap-2 overflow-x-auto border-b border-lav-100 p-4 no-scrollbar">
              {SIZE_TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`size-guide-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                    tab === t.id
                      ? "bg-blush-500 text-white"
                      : "text-muted hover:bg-blush-50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-sm" data-testid="size-table">
                <thead>
                  <tr className="border-b border-lav-100 text-xs font-bold uppercase tracking-[0.14em] text-muted">
                    <th className="px-6 py-4">Size</th>
                    {activeTab.cols.map(([label]) => (
                      <th key={label} className="px-6 py-4">
                        {label} {label === "Height" ? `(${unit})` : `(${unit})`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SIZE_ROWS.map((r) => (
                    <tr key={r.label} className="border-b border-lav-50 last:border-0">
                      <td className="px-6 py-4 font-display text-base font-semibold text-ink">
                        {r.label}
                      </td>
                      {activeTab.cols.map(([, key]) => (
                        <td key={key} className="px-6 py-4 text-ink/75">
                          {key === "height" ? fmtHeight(r.height) : fmt(r[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, ease: easeOut, delay: 0.08 }}
            className="rounded-[32px] bg-ink p-8 text-white lg:col-span-4"
            data-testid="size-calculator-card"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
              <Ruler className="h-5 w-5 text-blush-300" />
            </span>
            <h3 className="mt-5 font-display text-xl font-semibold">
              Not sure? Ask our fit finder
            </h3>
            <p className="mt-2 text-sm text-white/60">
              Enter your child's height and we'll suggest the perfect size.
            </p>
            <input
              type="number"
              min="50"
              max="170"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder={`Height in ${unit === "cm" ? "cm" : "inches"} (e.g. ${unit === "cm" ? "112" : "44"})`}
              data-testid="size-calculator-input"
              className="mt-6 w-full rounded-full border border-white/15 bg-white/10 px-5 py-3.5 text-sm text-white outline-none transition-colors duration-200 placeholder:text-white/40 focus:border-blush-300"
            />
            {recommended ? (
              <div className="mt-5 rounded-2xl bg-white/10 p-5" data-testid="size-calculator-result">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-blush-300">
                  Recommended size
                </p>
                <p className="mt-1 font-display text-3xl font-semibold">
                  {recommended}
                </p>
              </div>
            ) : (
              <p className="mt-5 flex items-center gap-2 text-sm text-white/50">
                <Sparkles className="h-4 w-4 text-butter-300" />
                Type a height to see the recommendation appear.
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: products = [], isLoading } = useSWR(`${API}/products`, fetcher);

  useSeo({
    title: "Children's Clothing Online UK — Shop All",
    description:
      "Browse children's clothing online UK: kids' school bags, children's socks, kids' underwear, baby clothes, children's leggings and more. Free UK delivery over £50.",
    image: products[0]?.image,
    jsonLd: productJsonLd(products, "Children's Clothing"),
  });
  const activeCat = searchParams.get("cat") || "all";

  const filtered =
    activeCat === "all"
      ? products
      : products.filter((p) => p.category_id === activeCat);

  const setFilter = (id) => {
    if (id === "all") setSearchParams({}, { replace: true });
    else setSearchParams({ cat: id }, { replace: true });
  };

  return (
    <main data-testid="shop-page">
      <section className="bg-gradient-to-b from-blush-50 via-lav-50/40 to-cream pb-10 pt-32 md:pt-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: easeOut }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
              Shop Sparkwear
            </p>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Our Collection
            </h1>
            <p className="mt-4 max-w-lg leading-relaxed text-muted">
              Every piece pastel-perfect, play-proof and parent-approved —
              from first onesies to school-ready essentials.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-8" data-testid="shop-products-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div
            className="flex gap-2 overflow-x-auto pb-2 no-scrollbar"
            data-testid="category-filter-bar"
          >
            {[{ id: "all", name: "All" }, ...CATEGORIES].map((c) => (
              <button
                key={c.id}
                data-testid={`filter-tab-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  activeCat === c.id
                    ? "bg-ink text-white shadow-md shadow-ink/15"
                    : "border border-lav-100 bg-white text-muted hover:border-blush-300 hover:text-ink"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <p className="mt-4 text-sm text-muted" data-testid="product-count">
            {isLoading
              ? "Loading sparkly things..."
              : `${filtered.length} sparkly piece${filtered.length === 1 ? "" : "s"}`}
          </p>

          {isLoading ? (
            <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] w-full rounded-[28px] bg-blush-50" />
                  <div className="mt-4 h-4 w-3/4 rounded-full bg-blush-50" />
                  <div className="mt-2 h-4 w-1/2 rounded-full bg-blush-50" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="mt-16 flex flex-col items-center gap-3 rounded-[32px] border border-dashed border-lav-300 bg-white/60 py-20 text-center">
              <PackageSearch className="h-10 w-10 text-lav-500" />
              <p className="font-display text-xl font-semibold text-ink">
                Nothing here yet
              </p>
              <p className="max-w-xs text-sm text-muted">
                This collection is being restocked with sparkles — check back
                soon or browse everything.
              </p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <DeliveryReturns />
      <SizeGuide />
    </main>
  );
}
